import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  background: var(--carbon-gradient);
  padding: var(--space-20) 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23333" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-5xl);
  font-weight: var(--font-black);
  margin-bottom: var(--space-6);
  background: linear-gradient(135deg, var(--white) 0%, var(--racing-red) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const HeroSubtitle = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-xl);
  font-weight: var(--font-medium);
  max-width: 800px;
  margin: 0 auto;
  line-height: var(--leading-relaxed);
  
  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-12);
`;

const Section = styled.section`
  background: var(--white);
  border-radius: var(--radius-2xl);
  padding: var(--space-10);
  box-shadow: var(--shadow-lg);
  
  @media (max-width: 768px) {
    padding: var(--space-8);
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-3xl);
  font-weight: var(--font-black);
  color: var(--carbon-black);
  margin-bottom: var(--space-6);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 4px;
    background: var(--racing-gradient);
    border-radius: var(--radius-full);
  }
`;

const SectionContent = styled.div`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--dark-gray);
  
  p {
    margin-bottom: var(--space-6);
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: var(--space-6) 0;
    
    li {
      position: relative;
      padding-left: var(--space-6);
      margin-bottom: var(--space-3);
      
      &::before {
        content: '⚡';
        position: absolute;
        left: 0;
        color: var(--racing-red);
        font-weight: bold;
      }
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-8);
  margin: var(--space-12) 0;
`;

const StatCard = styled.div`
  text-align: center;
  padding: var(--space-6);
  background: var(--light-gray);
  border-radius: var(--radius-xl);
  transition: transform var(--transition-fast);
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const StatNumber = styled.div`
  font-size: var(--text-4xl);
  font-weight: var(--font-black);
  color: var(--racing-red);
  margin-bottom: var(--space-2);
`;

const StatLabel = styled.div`
  color: var(--carbon-black);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-8);
  margin-top: var(--space-8);
`;

const TeamCard = styled.div`
  text-align: center;
  padding: var(--space-6);
  background: var(--light-gray);
  border-radius: var(--radius-xl);
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

const TeamImage = styled.div`
  width: 100px;
  height: 100px;
  background: var(--racing-gradient);
  border-radius: var(--radius-full);
  margin: 0 auto var(--space-4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-2xl);
  color: var(--white);
`;

const TeamName = styled.h3`
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-2);
`;

const TeamRole = styled.p`
  color: var(--racing-red);
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-3);
`;

const TeamBio = styled.p`
  color: var(--medium-gray);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  margin: 0;
`;

const AboutPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>About CarBuzz.fit</HeroTitle>
          <HeroSubtitle>
            We're passionate automotive enthusiasts dedicated to bringing you the most comprehensive, 
            accurate, and engaging automotive content. From cutting-edge reviews to industry insights, 
            we fuel your passion for cars with expert analysis and the latest trends.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        <ContentGrid>
          <Section>
            <SectionTitle>Our Mission</SectionTitle>
            <SectionContent>
              <p>
                At CarBuzz.fit, our mission is to be the definitive source for automotive excellence. 
                We believe that cars are more than just transportation—they're expressions of innovation, 
                design, and human ingenuity. Our team of expert journalists, engineers, and automotive 
                enthusiasts work tirelessly to deliver content that informs, inspires, and ignites your 
                passion for the automotive world.
              </p>
              <p>
                We're committed to providing unbiased, comprehensive coverage of the automotive industry, 
                from mainstream vehicles that define everyday transportation to exotic supercars that push 
                the boundaries of performance and technology. Our content spans every aspect of automotive 
                culture, including detailed reviews, technical analysis, industry trends, and the stories 
                behind the machines that move us.
              </p>
            </SectionContent>
            
            <StatsGrid>
              <StatCard>
                <StatNumber>500+</StatNumber>
                <StatLabel>Car Reviews</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>1M+</StatNumber>
                <StatLabel>Monthly Readers</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>50+</StatNumber>
                <StatLabel>Expert Contributors</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>24/7</StatNumber>
                <StatLabel>Industry Coverage</StatLabel>
              </StatCard>
            </StatsGrid>
          </Section>

          <Section>
            <SectionTitle>What Sets Us Apart</SectionTitle>
            <SectionContent>
              <p>
                Our approach to automotive journalism combines technical expertise with genuine passion. 
                We don't just report on cars—we understand them. Our team includes former automotive 
                engineers, professional racing drivers, and industry insiders who bring unique perspectives 
                to every story we tell.
              </p>
              
              <ul>
                <li>In-depth technical analysis backed by engineering expertise</li>
                <li>Comprehensive real-world testing across diverse conditions</li>
                <li>Exclusive access to manufacturers, designers, and industry leaders</li>
                <li>Unbiased reviews free from manufacturer influence</li>
                <li>Coverage spanning all market segments from economy to exotic</li>
                <li>Focus on both current vehicles and automotive heritage</li>
                <li>Environmental impact analysis and sustainable mobility coverage</li>
                <li>Global perspective on automotive trends and regulations</li>
              </ul>
              
              <p>
                We believe in transparency and integrity. Our editorial independence ensures that our 
                reviews and analysis reflect genuine assessments rather than marketing messages. When 
                we praise a vehicle, it's because it truly excels. When we identify shortcomings, 
                we explain them constructively to help readers make informed decisions.
              </p>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Our Editorial Standards</SectionTitle>
            <SectionContent>
              <p>
                CarBuzz.fit maintains the highest editorial standards in automotive journalism. Our content 
                is thoroughly researched, fact-checked, and reviewed by multiple experts before publication. 
                We adhere to strict ethical guidelines that ensure our independence and credibility.
              </p>
              
              <p>
                <strong>Editorial Independence:</strong> We maintain complete editorial independence from 
                automotive manufacturers, dealers, and advertisers. Our opinions and recommendations are 
                based solely on merit and performance.
              </p>
              
              <p>
                <strong>Accuracy and Verification:</strong> All technical specifications, performance figures, 
                and factual claims are verified through multiple sources, including manufacturer data, 
                independent testing, and direct measurement when possible.
              </p>
              
              <p>
                <strong>Transparency:</strong> We clearly disclose any potential conflicts of interest, 
                sponsored content, or material relationships that might influence our coverage.
              </p>
              
              <p>
                <strong>Continuous Learning:</strong> The automotive industry evolves rapidly, and we're 
                committed to staying current with the latest technologies, regulations, and market trends 
                through ongoing education and industry engagement.
              </p>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Our Expertise</SectionTitle>
            <SectionContent>
              <p>
                CarBuzz.fit is built on a foundation of automotive expertise spanning multiple disciplines. 
                Our content is created by professionals with real-world experience in automotive engineering, 
                motorsports, journalism, and industry analysis.
              </p>
              
              <p>
                <strong>Editorial Excellence:</strong> Our content undergoes rigorous fact-checking and 
                technical review to ensure accuracy and relevance. We maintain relationships with automotive 
                manufacturers, racing teams, and industry experts to provide insider perspectives and 
                exclusive insights.
              </p>
              
              <p>
                <strong>Technical Authority:</strong> Complex automotive technologies are explained by 
                professionals who understand the engineering principles behind modern vehicles. From 
                hybrid powertrains to advanced driver assistance systems, we break down technical 
                concepts into accessible content.
              </p>
              
              <p>
                <strong>Global Perspective:</strong> Our coverage spans automotive markets worldwide, 
                from emerging electric vehicle technologies in Asia to traditional luxury craftsmanship 
                in Europe and innovative American performance engineering.
              </p>
              
              <p>
                <strong>Future Focus:</strong> We don't just cover current vehicles—we analyze trends, 
                emerging technologies, and the future of mobility to help readers understand where 
                the automotive industry is heading.
              </p>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <SectionContent>
              <p>
                We value feedback from our readers and welcome questions, suggestions, and story ideas. 
                Our team is committed to engaging with the automotive community and continuously improving 
                our content based on reader input.
              </p>
              
              <p>
                <strong>Editorial Inquiries:</strong> editorial@carbuzz.fit<br/>
                <strong>Technical Questions:</strong> tech@carbuzz.fit<br/>
                <strong>Press Releases:</strong> press@carbuzz.fit<br/>
                <strong>General Information:</strong> info@carbuzz.fit
              </p>
              
              <p>
                Follow us on social media for the latest automotive news, behind-the-scenes content, 
                and exclusive insights from our team of experts. Join our community of automotive 
                enthusiasts and stay connected with the latest developments in the automotive world.
              </p>
            </SectionContent>
          </Section>
        </ContentGrid>
      </ContentContainer>
    </PageContainer>
  );
};

export default AboutPage;
